/**
 * @fileoverview Footer bar shared by every sales table (Active Clients, My Page, Sales Pipeline,
 * Sales Team, User Management).
 * @description The count line ("Showing X of Y …") plus the bundle design's static "Page 1 of 1"
 * label — every one of these tables is a single unpaged page over the full mock dataset, so real
 * pagination was never built, but the label itself is still part of the design and was getting
 * silently dropped page by page rather than each page re-implementing this same two-span row.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

interface TableFooterProps {
  line: string
}

/**
 * @component
 * @param {TableFooterProps} props - The already-translated count line to show on the left.
 * @param {string} props.line - E.g. "Showing 12 of 12 clients".
 */
function TableFooter({ line }: TableFooterProps) {
  const { t } = useTranslation()

  return (
    <div
      className="flex flex-wrap items-center gap-3 border-t border-divider bg-surface-hover px-4 py-2.5 sm:px-5"
      data-testid="table-footer"
    >
      <span className="flex-1 text-xs text-slate">{line}</span>
      <span className="text-xs font-medium text-placeholder">{t('sales.pageOfPages')}</span>
    </div>
  )
}

export default TableFooter
